import { VIEW_AS_PATH, viewAsId } from './viewAs';

/** Rule refusals come back as `{ error, problems, fields }`; keep them whole so forms can show them. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problems: string[] = [],
    readonly fields: Record<string, string> = {},
    /** The server's reference for an unexpected failure, so the office can find it in the logs. */
    readonly requestId = '',
  ) {
    super(message);
  }
}

/** Something answered, but not the API: a proxy error page, or a hotel wifi sign-in page. */
export class UnreadableReply extends ApiError {}

/** Parse a reply body; a proxy or crash page is HTML, not JSON, and must not look like no signal. */
function parse(text: string): Record<string, unknown> | null {
  if (!text) return {};
  try {
    const data: unknown = JSON.parse(text);
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  } catch {
    return null;
  }
}

/**
 * Told when the server says the session is gone (expired, revoked, user switched off), so the
 * app can show the sign-in instead of pages that cannot load. The sign-in routes' own 401s
 * (a wrong password, the start-up check) are their callers' to handle.
 */
const lostSession = new Set<() => void>();
export function onSessionLost(fn: () => void) {
  lostSession.add(fn);
  return () => void lostSession.delete(fn);
}
let session = 0;
/** A new sign-in: a 401 to a request sent before it was about the old session, not this one. */
export function sessionStarted() {
  session++;
}

/**
 * `timeoutMs` gives up on a request that gets no answer (one bar of signal), so it fails the
 * way no signal does. Only for requests that are safe to send again.
 */
export async function api<T>(
  method: string,
  path: string,
  body?: unknown,
  timeoutMs?: number,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  const as = viewAsId();
  if (as !== null && VIEW_AS_PATH.test(path)) headers['x-view-as'] = String(as);
  const sentIn = session;
  // A timer rather than AbortSignal.timeout, which older iPhones do not have.
  const stop = timeoutMs ? new AbortController() : null;
  const timer = stop ? setTimeout(() => stop.abort(), timeoutMs) : undefined;
  let res: Response;
  let text: string;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: Object.keys(headers).length ? headers : undefined,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: stop?.signal,
    });
    text = await res.text();
  } finally {
    clearTimeout(timer);
  }
  const data = parse(text);
  if (data === null) {
    // The server (or something in front of it) answered, just not in a way the app can read.
    throw new UnreadableReply(
      `The server sent a reply the app could not read (${res.status}).`,
      res.status,
    );
  }
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/api/auth/') && sentIn === session) {
      for (const fn of lostSession) fn();
    }
    throw new ApiError(
      typeof data.error === 'string' ? data.error : `Request failed (${res.status})`,
      res.status,
      Array.isArray(data.problems) ? (data.problems as string[]) : [],
      (data.fields as Record<string, string> | undefined) ?? {},
      typeof data.requestId === 'string' ? data.requestId : '',
    );
  }
  return data as T;
}

export const get = <T>(path: string, timeoutMs?: number) =>
  api<T>('GET', path, undefined, timeoutMs);
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body ?? {});
export const put = <T>(path: string, body: unknown) => api<T>('PUT', path, body);
